import type { ExerciseTemplate } from '@yuan-math/exercises';
import { choiceExercise, expr, expressionExercise, leadingTerm, numericExercise, options, pickDistinct, signedConstant, signedTerm, template, trap } from './authoring.ts';

/**
 * Polynomials and factoring.
 *
 * Factoring answers are graded with `requireFactored`, because numeric
 * equivalence alone would accept the expanded form — (x+2)(x+3) and x²+5x+6
 * agree at every value of x, and the whole point of the exercise is the shape.
 */
export const POLYNOMIAL_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'polynomial-add',
    conceptId: 'polynomials',
    description: 'Add or subtract two polynomials by collecting like terms.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const limit = difficulty === 2 ? 6 : 12;
      const a1 = rng.int(1, limit);
      const b1 = rng.int(-limit, limit);
      const a2 = rng.int(1, limit);
      const b2 = rng.int(-limit, limit);
      const subtracting = difficulty === 3 && rng.bool();
      const sign = subtracting ? -1 : 1;
      const a = a1 + sign * a2;
      const b = b1 + sign * b2;

      return numericExercise({
        prompt: `${subtracting ? 'Subtract the second polynomial from the first' : 'Add these polynomials'}. What is the coefficient of x?`,
        latex: expr(
          `(${leadingTerm(a1, 'x^2')}`,
          signedTerm(b1),
          `) ${subtracting ? '-' : '+'} (${leadingTerm(a2, 'x^2')}`,
          signedTerm(b2),
          ')',
        ).replace(/\( /g, '(').replace(/ \)/g, ')'),
        value: b,
        summary: `Only like terms combine: ${b1} ${subtracting ? '−' : '+'} ${b2} = ${b}, so the x term is ${b}x.`,
        hint: 'x² terms combine with x² terms, and x terms with x terms. They never mix.',
        steps: [
          { label: 'Collect the x² terms.', latex: `${a}x^2` },
          { label: 'Collect the x terms.', latex: `${b}x` },
        ],
        // Adding the x² coefficients instead, or ignoring the subtraction.
        traps: [
          ...(a === b ? [] : [trap(a, 'variable-term-left-behind')]),
          ...(subtracting && b1 + b2 !== b ? [trap(b1 + b2, 'subtracting-negative')] : []),
        ],
      });
    },
  }),

  template({
    id: 'polynomial-multiply-binomials',
    conceptId: 'polynomials',
    description: 'Multiply two binomials, where every term meets every term.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const limit = difficulty === 4 ? 9 : 5;
      const p = rng.int(-limit, limit) || 2;
      const q = rng.int(-limit, limit) || 3;
      const middle = p + q;
      const last = p * q;

      return numericExercise({
        prompt: 'Expand this. What is the coefficient of x in the answer?',
        latex: `(x ${signedConstant(p)})(x ${signedConstant(q)})`,
        value: middle,
        summary: `Every term meets every term: x² ${signedTerm(q)} ${signedTerm(p)} ${signedConstant(last)}, so the x terms give ${p} + ${q} = ${middle}.`,
        hint: 'There are four products, and two of them give an x term. Add those two.',
        steps: [
          { label: 'Multiply the first terms.', latex: `x \\times x = x^2` },
          { label: 'The two cross products both give x terms.', latex: `${q}x ${signedTerm(p)}` },
          { label: 'Add them.', latex: `${middle}x` },
          { label: 'And the last terms.', latex: `${p} \\times ${q} = ${last}` },
        ],
        traps: [...(last === middle ? [] : [trap(last, 'binomial-square-no-middle-term')])],
      });
    },
  }),

  template({
    id: 'polynomial-square-binomial',
    conceptId: 'polynomials',
    description: 'Square a binomial, where the middle term is the whole test.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const b = rng.int(2, 9) * (rng.bool() ? 1 : -1);
      const middle = 2 * b;
      const last = b * b;

      const { choices, correctId } = options(
        [
          { latex: `x^2 ${signedTerm(middle)} ${signedConstant(last)}`, correct: true },
          { latex: `x^2 ${signedConstant(last)}`, misconceptionId: 'binomial-square-no-middle-term' },
          { latex: `x^2 ${signedTerm(b)} ${signedConstant(last)}`, misconceptionId: 'binomial-square-no-middle-term' },
          { latex: `x^2 ${signedTerm(middle)} ${signedConstant(2 * b)}`, misconceptionId: 'exponents-multiplied-not-added' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Expand this.',
        latex: `(x ${signedConstant(b)})^2`,
        choices,
        correctId,
        summary: `(x ${signedConstant(b)})² means (x ${signedConstant(b)})(x ${signedConstant(b)}), which gives x² ${signedTerm(middle)} ${signedConstant(last)}.`,
        hint: 'A square is the bracket multiplied by itself. Write it out as two brackets before expanding.',
        steps: [
          { label: 'Write the square as two brackets.', latex: `(x ${signedConstant(b)})(x ${signedConstant(b)})` },
          { label: 'Every term meets every term.', latex: `x^2 ${signedTerm(b)} ${signedTerm(b)} ${signedConstant(last)}` },
          { label: 'Collect the middle.', latex: `x^2 ${signedTerm(middle)} ${signedConstant(last)}` },
        ],
      });
    },
  }),

  template({
    id: 'factor-common-factor',
    conceptId: 'factoring',
    description: 'Take out the greatest common factor.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const factor = rng.int(2, difficulty === 2 ? 6 : 12);
      const a = rng.int(2, 9);
      const b = rng.int(2, 9);

      return expressionExercise({
        prompt: 'Factorise fully by taking out the greatest common factor.',
        latex: `${factor * a}x + ${factor * b}`,
        canonical: `${factor} * (${a} * x + ${b})`,
        requireFactored: true,
        summary: `${factor} divides both terms, leaving ${factor}(${a}x + ${b}).`,
        hint: `What is the largest number that divides both ${factor * a} and ${factor * b}?`,
        steps: [
          { label: 'Find the greatest common factor.', latex: `\\gcd(${factor * a}, ${factor * b}) = ${factor}` },
          { label: 'Divide each term by it.', latex: `${a}x + ${b}` },
          { label: 'Write it as a product.', latex: `${factor}(${a}x + ${b})` },
        ],
      });
    },
  }),

  template({
    id: 'factor-quadratic',
    conceptId: 'factoring',
    description: 'Factorise a monic quadratic into two brackets.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const limit = difficulty === 4 ? 9 : 6;
      const p = rng.int(1, limit) * (rng.bool() ? 1 : -1);
      // Equal values make it a perfect square, which is a different lesson;
      // opposite values make the middle term vanish, which is the difference of
      // two squares and has its own template.
      const q = pickDistinct(rng, limit, [p, -p]);
      const middle = p + q;
      const last = p * q;

      return expressionExercise({
        prompt: 'Factorise this into two brackets.',
        latex: expr('x^2', signedTerm(middle), signedConstant(last)),
        canonical: `(x + ${p}) * (x + ${q})`,
        requireFactored: true,
        summary: `Two numbers multiplying to ${last} and adding to ${middle}: ${p} and ${q}. So it factorises as (x ${signedConstant(p)})(x ${signedConstant(q)}).`,
        hint: `Look for two numbers that multiply to ${last} and add to ${middle}.`,
        steps: [
          { label: 'Find two numbers with the right product and sum.', latex: `${p} \\times ${q} = ${last},\\quad ${p} + ${q} = ${middle}` },
          { label: 'Put them into the brackets.', latex: `(x ${signedConstant(p)})(x ${signedConstant(q)})` },
          { label: 'Check by expanding.', latex: `x^2 ${signedTerm(middle)} ${signedConstant(last)}` },
        ],
      });
    },
  }),

  template({
    id: 'factor-difference-of-squares',
    conceptId: 'factoring',
    description: 'Recognise and factorise a difference of two squares.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const n = rng.int(2, 12);

      return expressionExercise({
        prompt: 'Factorise this.',
        latex: `x^2 - ${n * n}`,
        canonical: `(x + ${n}) * (x - ${n})`,
        requireFactored: true,
        summary: `A difference of two squares: x² − ${n}² = (x + ${n})(x − ${n}).`,
        hint: 'There is no x term. Both parts are perfect squares, and they are being subtracted.',
        steps: [
          { label: 'Recognise both as squares.', latex: `x^2 - ${n}^2` },
          { label: 'Apply the pattern a² − b² = (a + b)(a − b).', latex: `(x + ${n})(x - ${n})` },
        ],
      });
    },
  }),

  template({
    id: 'factor-identify',
    conceptId: 'factoring',
    description: 'Pick the correct factorisation from four candidates.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const p = rng.int(1, 8);
      const q = rng.int(1, 8) + p;
      const middle = p + q;
      const last = p * q;

      const { choices, correctId } = options(
        [
          { latex: `(x + ${p})(x + ${q})`, correct: true },
          { latex: `(x - ${p})(x - ${q})`, misconceptionId: 'sign-error-in-expansion' },
          { latex: `(x + ${p})(x - ${q})`, misconceptionId: 'sign-error-in-expansion' },
          { latex: `x^2 ${signedTerm(middle)} ${signedConstant(last)}`, misconceptionId: 'left-unfactored' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which of these is the factorisation of the expression below?',
        latex: `x^2 + ${middle}x + ${last}`,
        choices,
        correctId,
        summary: `${p} and ${q} multiply to ${last} and add to ${middle}, and both signs are positive.`,
        hint: 'Expand each candidate mentally and see which one comes back to the original.',
      });
    },
  }),
];
